"use client"
import { HeroFlow } from "../components/hero-flow"
import { HeroBackground } from "../components/hero-background"
import { Link, useT } from "../lib/router"
import { coreServices } from "../content/services"
import { solutions, caseStudies } from "../content/catalog"
import { CEOMessage } from "../components/ceo-message"
import {
  Button,
  Container,
  CTASection,
  Eyebrow,
  Icon,
  LinkCard,
  Section,
  SectionHeading,
} from "../components/ui"

export default function Home() {
  const t = useT()
  const publishedWork = caseStudies.filter((c) => c.published)

  const reasons = [
    { n: "01", title: { en: "Business comes first", ar: "نفهم عملك أولًا" }, body: { en: "We learn your goals, customers and daily work before choosing technology.", ar: "نفهم أهدافك وعملاءك وطبيعة عملك قبل اختيار التقنية المناسبة." } },
    { n: "02", title: { en: "Connected by design", ar: "حلول متكاملة لا أدوات منفصلة" }, body: { en: "We connect systems and information to make everyday work easier.", ar: "نربط الأنظمة والمعلومات لتصبح الأعمال اليومية أكثر تنظيمًا وسهولة." } },
    { n: "03", title: { en: "Clear steps, visible progress", ar: "خطوات واضحة وتقدم ملموس" }, body: { en: "We agree priorities, delivery stages and review points from the outset.", ar: "نتفق معك على الأولويات ومراحل التسليم ونقاط المراجعة منذ البداية." } },
    { n: "04", title: { en: "Made to grow with you", ar: "تتطور مع نمو أعمالك" }, body: { en: "We design for your needs today with room to improve and expand later.", ar: "نصمم لما تحتاجه اليوم مع قابلية التحسين والتوسع مستقبلًا." } },
  ]

  return (
    <>
      {}
      <section className="relative overflow-hidden bg-white">
        <HeroBackground />
        <Container className="relative grid items-center gap-12 pb-20 pt-14 md:pb-28 md:pt-20 lg:min-h-[calc(100svh-73px)] lg:grid-cols-2 lg:gap-8 lg:py-16">
          <div className="min-w-0 rise">
            <Eyebrow>
              {t({
                en: "Digital Solutions for Your Business",
                ar: "حلول رقمية لأعمالك",
              })}
            </Eyebrow>
            <h1 className="brand-h1 mt-6 font-medium tracking-tight text-ink">
              {t({
                en: "Many paths to your goal. We choose and build the right one for your needs.",
                ar: "طرق كثيرة للوصول لهدفك، لكننا نختار ونبني لك الأنسب وفق احتياجاتك.",
              })}
            </h1>
            <p className="mt-6 max-w-xl text-lg text-slate md:text-xl md:leading-8">
              {t({
                en: "From idea to execution, we turn complex technology choices into a clear path shaped around your business goals.",
                ar: "من الفكرة إلى التنفيذ، نحوّل الخيارات التقنية المعقدة إلى مسار واضح يناسب أهداف عملك.",
              })}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button to="/start-project">
                {t({
                  en: "Start your project journey",
                  ar: "ابدأ مسار مشروعك",
                })}
              </Button>
              <Button to="/solutions" variant="secondary">
                {t({ en: "Explore our solutions", ar: "استكشف حلولنا" })}
              </Button>
            </div>
            <p className="mt-10 text-base text-slate">
              {t({
                en: "From digital presence to intelligent operations.",
                ar: "من التواجد الرقمي إلى العمليات الذكية.",
              })}
            </p>
          </div>
          <div className="min-w-0">
            <HeroFlow />
          </div>
        </Container>
      </section>

      <Section id="about-home">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="min-w-0 lg:col-span-7">
            <Eyebrow>{t({ en: "About ORKEST", ar: "عن أوركست" })}</Eyebrow>
            <h2 className="brand-h2 mt-4 font-medium tracking-tight text-ink">
              {t({ en: "Who we are", ar: "من نحن" })}
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-8 text-slate md:text-lg">
              {t({
                en: "ORKEST Digital Solutions develops websites, applications and connected business systems. We bring design and technology together to turn business needs into practical digital solutions.",
                ar: "أوركست للحلول الرقمية تطور مواقع الويب والتطبيقات وأنظمة الأعمال المترابطة. نجمع بين التصميم والتقنية لتحويل احتياجات الشركات إلى حلول رقمية عملية.",
              })}
            </p>
            <div className="mt-8 grid gap-6 border-t border-line pt-7 sm:grid-cols-2">
              <div>
                <h3 className="text-xl font-medium text-ink">{t({ en: "Our vision", ar: "رؤيتنا" })}</h3>
                <p className="mt-3 text-base leading-7 text-slate">
                  {t({
                    en: "Digital technology that makes work simpler, keeps information connected and helps businesses grow.",
                    ar: "تكنولوجيا رقمية تجعل العمل أبسط والمعلومات مترابطة وتساعد الشركات على النمو.",
                  })}
                </p>
              </div>
              <div>
                <h3 className="text-xl font-medium text-ink">{t({ en: "Our mission", ar: "هدفنا" })}</h3>
                <p className="mt-3 text-base leading-7 text-slate">
                  {t({
                    en: "Understand each business, build the right digital solution and support it from planning to launch and beyond.",
                    ar: "فهم احتياجات كل شركة وبناء الحل الرقمي المناسب لها ودعمه من التخطيط إلى الإطلاق وما بعده.",
                  })}
                </p>
              </div>
            </div>
            <Button to="/about" variant="ghost" className="mt-6">
              {t({ en: "Get to know ORKEST", ar: "تعرّف على أوركست" })}
            </Button>
          </div>
          <figure className="min-w-0 overflow-hidden rounded-r24 border border-line bg-mist lg:col-span-5">
            <img
              src="/brand/hero-flow-v1.webp"
              width={1448}
              height={1086}
              loading="lazy"
              decoding="async"
              alt={t({
                en: "ORKEST brand artwork with connected blue pathways",
                ar: "لوحة من هوية أوركست البصرية تُظهر مسارات زرقاء مترابطة",
              })}
              className="aspect-[5/4] w-full object-cover"
            />
            <figcaption className="border-t border-line bg-white px-6 py-5">
              <p className="text-sm font-medium text-blue">ORKEST Digital Solutions</p>
              <p className="mt-1 text-base text-ink">
                {t({ en: "One vision. Connected solutions.", ar: "رؤية واحدة. حلول مترابطة." })}
              </p>
            </figcaption>
          </figure>
        </div>
      </Section>


      <Section>
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading
            eyebrow={t({ en: "Core services", ar: "خدماتنا الأساسية" })}
            title={t({
              en: "Many capabilities. One ORKEST.",
              ar: "خدمات متعددة. شريك واحد.",
            })}
            lead={t({
              en: "Choose one service or combine several. We agree the work around your goals and budget.",
              ar: "اختر خدمة واحدة أو اجمع عدة خدمات. نتفق على العمل حسب أهدافك وميزانيتك.",
            })}
          />
          <Button to="/services" variant="ghost" className="shrink-0 self-start whitespace-nowrap md:self-auto">
            {t({ en: "All services", ar: "جميع الخدمات" })}
          </Button>
        </div>
        <ol className="mt-10 grid gap-4 sm:grid-cols-2 md:mt-12 md:gap-6 lg:grid-cols-3">
          {coreServices.map((s, i) => (
            <li key={s.slug} className="min-w-0">
              <Link
                to={`/services/${s.slug}`}
                className="group flex h-full flex-col rounded-r16 border border-line bg-white p-6 transition-colors hover:border-blue hover:bg-mist md:p-8"
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-r8 bg-ice text-blue">
                    <Icon name={s.icon} />
                  </span>
                  <span aria-hidden="true" dir="ltr" className="inline-flex h-9 min-w-12 items-center justify-center rounded-r8 border border-line px-3 font-sans text-sm font-medium tabular-nums text-slate">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-6 text-[22px] font-medium leading-normal text-ink md:text-2xl">{t(s.title)}</h3>
                <p className="mt-3 flex-1 text-base leading-7 text-slate">{t(s.summary)}</p>
                <div className="mt-6 flex items-center justify-between gap-3 border-t border-line pt-5 text-base font-medium text-blue">
                  <span>{t({ en: "Learn more", ar: "اعرف المزيد" })}</span>
                  <Icon name="arrow" flip className="size-5 shrink-0 transition-transform motion-reduce:transition-none ltr:group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
                </div>
              </Link>
            </li>
          ))}
        </ol>
      </Section>

      {}
      <Section>
        <SectionHeading
          eyebrow={t({ en: "Industry solutions", ar: "حلول القطاعات" })}
          title={t({
            en: "Built around how your industry works.",
            ar: "مبنية حول طريقة عمل قطاعك.",
          })}
          lead={t({
            en: "Solutions for the daily challenges your customers and team face.",
            ar: "حلول للتحديات اليومية التي يواجهها عملاؤك وفريقك.",
          })}
        />
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {solutions.map((s) => (
            <LinkCard
              key={s.slug}
              to={`/solutions/${s.slug}`}
              title={t(s.title)}
              body={t(s.summary)}
            />
          ))}
        </div>
      </Section>

      {}
      {publishedWork.length > 0 && (
        <Section tone="mist">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <SectionHeading
              eyebrow={t({ en: "Selected work", ar: "أعمال مختارة" })}
              title={t({
                en: "Work we are proud of.",
                ar: "أعمال نعتز بها.",
              })}
              lead={t({
                en: "A selection of projects and the needs they address.",
                ar: "مجموعة من المشاريع والاحتياجات التي تلبيها.",
              })}
            />
            <Button to="/work" variant="ghost">
              {t({ en: "All work", ar: "جميع الأعمال" })}
            </Button>
          </div>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {publishedWork.map((c) => (
              <LinkCard
                key={c.slug}
                to={`/work/${c.slug}`}
                eyebrow={t(c.industry)}
                title={t(c.title)}
                body={t(c.challenge)}
              />
            ))}
          </div>
        </Section>
      )}

      {}
      <Section tone="mist" id="why-orkest">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="relative overflow-hidden rounded-r24 bg-blue p-8 text-white md:p-10 lg:col-span-5 lg:p-12">
            <Eyebrow light>{t({ en: "Why ORKEST", ar: "لماذا أوركست؟" })}</Eyebrow>
            <h2 className="brand-h2 mt-5 font-medium tracking-tight">
              {t({
                en: "The right solution starts with understanding the problem.",
                ar: "الحل المناسب يبدأ بفهم المشكلة.",
              })}
            </h2>
            <p className="mt-6 text-base leading-8 text-white/90 md:text-lg">
              {t({
                en: "We bring business understanding, design and engineering together, helping you move from an idea to a useful, sustainable solution.",
                ar: "نجمع فهم الأعمال والتصميم والهندسة لمساعدتك على تحويل الفكرة إلى حل عملي قابل للاستمرار والتطوير.",
              })}
            </p>
            <span aria-hidden="true" className="pointer-events-none absolute -bottom-16 -end-16 size-48 rounded-full border-[22px] border-white/10" />
          </div>
          <ol className="overflow-hidden rounded-r24 border border-line bg-white lg:col-span-7">
            {reasons.map((reason, index) => (
              <li
                key={reason.n}
                className={"flex items-start gap-5 px-6 py-6 md:gap-7 md:px-9 md:py-7 " + (index > 0 ? "border-t border-line" : "")}
              >
                <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-r8 bg-ice text-sm font-medium tabular-nums text-blue">
                  {reason.n}
                </span>
                <div className="min-w-0">
                  <h3 className="text-lg font-medium text-ink md:text-xl">{t(reason.title)}</h3>
                  <p className="mt-2 text-base leading-7 text-slate">{t(reason.body)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Section>


      <CEOMessage />

      <CTASection
        id="composer"
        eyebrow="ORKEST Composer"
        title={t({
          en: "Where do you want your business to be next year?",
          ar: "أين تريد أن تكون أعمالك في العام القادم؟",
        })}
        body={t({
          en: "Start your project journey with ORKEST Composer. Choose your services, share your needs and inspiration, and let our team help you plan the next step.",
          ar: "ابدأ مسار مشروعك عبر ORKEST Composer. اختر الخدمات، وحدّد احتياجاتك، وأضف ما يلهمك؛ ليتواصل فريقنا معك لتحديد الخطوة التالية.",
        })}
        primaryLabel={t({
          en: "Start with ORKEST Composer",
          ar: "ابدأ مع ORKEST Composer",
        })}
      />
    </>
  )
}
